import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import {
  DEFAULT_MENTION_TEXT_VIEW,
  isMentionTextView,
  type MentionTextView
} from "@/shared/vocabulary/mention-text-views";

/**
 * A preferência da PESSOA de como o card de menção mostra o texto
 * (2026-10-31): a postagem ou o resumo da IA, em `/me/preferences`
 * (`mention_text_view`). Lida UMA vez ao montar a tela que a usa; leitura
 * que falha deixa o padrão (postagem) sem barulho — é uma conveniência,
 * não um dado que a tela precise pra funcionar.
 *
 * `change` é otimista: a tela troca na hora, grava, e se o servidor
 * recusar volta ao valor anterior e avisa por `onError`.
 */
export function useMentionTextView(token: string, onError: (message: string) => void) {
  const [view, setView] = useState<MentionTextView>(DEFAULT_MENTION_TEXT_VIEW);
  const viewRef = useRef(view);
  viewRef.current = view;
  const errorRef = useRef(onError);
  errorRef.current = onError;
  // Escolha feita antes de a leitura voltar vence a leitura.
  const touched = useRef(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    touched.current = false;
    apiClient
      .myPreferences(token)
      .then((preferences) => {
        if (alive && !touched.current && isMentionTextView(preferences.mention_text_view)) {
          setView(preferences.mention_text_view);
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [token]);

  const change = useCallback(
    async (next: MentionTextView) => {
      const previous = viewRef.current;
      if (next === previous) return;
      touched.current = true;
      setView(next);
      try {
        await apiClient.updateMyPreferences(token, { mention_text_view: next });
      } catch (failure) {
        setView(previous);
        errorRef.current(errorMessage(failure, "Não foi possível salvar a preferência."));
      }
    },
    [token]
  );

  return { view, change };
}
